import { destinationClient } from "@/api/grpc";
import { useAuthedRpc } from "@/hooks/common";
import { makeTripPath } from "@/lib/proto";
import { create } from "@bufbuild/protobuf";
import { DestinationSchema, DestinationStatus } from "@generated/proto/model";
import { CreateDestinationRequestSchema } from "@generated/proto/service";

interface CreateDestinationParams {
  /** Location display text (e.g., "San Francisco, CA") */
  location: string;
  /** Optional title for the destination */
  title?: string;
  /** Status - defaults to CONFIRMED */
  status?: DestinationStatus;
  /** Optional viewport center coordinates */
  viewport?: {
    latitude: number;
    longitude: number;
  };
}

/**
 * Hook for DestinationService.CreateDestination
 *
 * Creates a new destination within a trip.
 */
export function useCreateDestination() {
  const { run, loading, error } = useAuthedRpc(
    async (user, tripId: string, params: CreateDestinationParams) => {
      if (!tripId?.trim()) throw new Error("tripId is required.");
      if (!params.location?.trim()) throw new Error("location is required.");

      const request = create(CreateDestinationRequestSchema, {
        tripPath: makeTripPath(user.uid, tripId),
        destination: create(DestinationSchema, {
          location: params.location,
          title: params.title || params.location,
          status: params.status ?? DestinationStatus.CONFIRMED,
          viewport: params.viewport
            ? {
                center: {
                  latitude: params.viewport.latitude,
                  longitude: params.viewport.longitude,
                },
              }
            : undefined,
        }),
      });

      // Type cast needed: @bufbuild/protobuf create() returns MessageInitShape
      // which doesn't match Connect-Web's expected Message type. This is a known
      // limitation and the pattern is used consistently across all hooks.
      return await destinationClient.createDestination(request as any);
    }
  );

  return { createDestination: run, loading, error };
}
